import { createPortal } from 'react-dom';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import clx from 'classnames';
import styles from './RecurrenceFrequencySelect.module.scss';

/**
 * Select custom per la ricorrenza: evita la <select> nativa
 * (Safari/iOS dark mode → campo nero/vuoto illeggibile).
 * Menu in portal così non viene tagliato dallo sheet modal.
 */
const RecurrenceFrequencySelect = ({
  label,
  value,
  options = [],
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selected =
    options.find((option) => Number(option.value) === Number(value)) || options[0];

  const close = useCallback(() => setOpen(false), []);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 6;
    const estimatedHeight = Math.min((options.length || 1) * 48 + 12, 240);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      zIndex: 10060,
    });
  }, [options.length]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      close();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const menu =
    open &&
    createPortal(
      <ul
        ref={menuRef}
        className={styles.menu}
        style={menuStyle}
        role="listbox"
      >
        {options.map((option) => {
          const isActive = Number(option.value) === Number(selected?.value);
          return (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={isActive}
                className={clx(styles.option, {
                  [styles.option_active]: isActive,
                })}
                onClick={() => {
                  onChange?.(Number(option.value));
                  close();
                }}
              >
                {option.name}
              </button>
            </li>
          );
        })}
      </ul>,
      document.body,
    );

  return (
    <div className={styles.root} ref={rootRef}>
      {label ? <span className={styles.label}>{label}</span> : null}
      <div className={styles.field}>
        <button
          type="button"
          ref={triggerRef}
          className={clx(styles.trigger, { [styles.trigger_open]: open })}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className={styles.trigger_text}>{selected?.name || '—'}</span>
          <ChevronDown
            className={styles.chevron}
            size={18}
            strokeWidth={2.2}
            aria-hidden
          />
        </button>
        {menu}
      </div>
    </div>
  );
};

export default RecurrenceFrequencySelect;
